import React from 'react';
import QRCode from 'react-qr-code';
import { CircleCheckBig } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import type { CreditDebitNoteHeaderDto, CreditDebitNoteDetailDto } from '../services/creditDebitNoteApi';
import { getCreditDebitNoteLineDescription } from '../services/creditDebitNoteApi';
import type { Branch } from '../services/branchApi';
import type { Customer } from '../services/customerApi';

function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function InvoiceStyleMetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-2 py-1.5 break-words bg-white">
      <span className="font-normal text-gray-900">{label}</span>
      <span className="font-bold text-gray-900">{value}</span>
    </div>
  );
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

export interface CreditDebitNoteEinvoiceChunkPageProps {
  copyLabel: string;
  chunk: CreditDebitNoteDetailDto[];
  globalStart: number;
  nextPageNum: number;
  breakAfter: React.CSSProperties['pageBreakAfter'];
  isLastChunk: boolean;
  docTitle: string;
  note: CreditDebitNoteHeaderDto;
  noteRec: Record<string, unknown>;
  qrValue: string;
  branchDetails: Branch | null;
  shipToCustomer: Customer | null;
  billToCustomer: Customer | null;
  isGst: boolean;
  taxableAmount: number;
  totalCgst: number;
  totalSgst: number;
  totalIgst: number;
  totalQty: number;
  grandTotal: number;
  totalTaxAmount: number;
  hsnWiseTaxDetails: HsnWiseRow[];
  einvoiceSignerName: string;
  digitallySignedOn?: string;
  branchDisplayName: string;
  amountInWords: (n: number) => string;
}

/**
 * One printed sheet: invoice-doc-header (repeats every page), invoice-doc-body (line chunk),
 * invoice-doc-footer only on the last chunk (totals, HSN, declaration) — same 3-part model as InvoiceChunkedPrint.
 */
export function CreditDebitNoteEinvoiceChunkPage({
  copyLabel,
  chunk,
  globalStart,
  nextPageNum,
  breakAfter,
  isLastChunk,
  docTitle,
  note,
  noteRec,
  qrValue,
  branchDetails,
  shipToCustomer,
  billToCustomer,
  isGst,
  taxableAmount,
  totalCgst,
  totalSgst,
  totalIgst,
  totalQty,
  grandTotal,
  totalTaxAmount,
  hsnWiseTaxDetails,
  einvoiceSignerName,
  digitallySignedOn,
  branchDisplayName,
  amountInWords,
}: CreditDebitNoteEinvoiceChunkPageProps) {
  return (
    <div
      className="invoice-a4-sheet invoice-chunk-page invoice-container bg-white"
      style={{ pageBreakAfter: breakAfter }}
    >
      {/* HEADER — repeated on every body page */}
      <div className="invoice-doc-header invoice-print-header-block print:break-inside-avoid">
        <div className="invoice-doc-title-row">
          <h2 className="invoice-doc-title">{docTitle}</h2>
          <div className="invoice-doc-copy-label">({copyLabel})</div>
        </div>
        <div className="invoice-einvoice-strip mb-4 print:break-inside-avoid">
          <div className="invoice-einvoice-inner">
            <div className="invoice-einvoice-left">
              <div />
              <table className="invoice-irn-table">
                <tbody>
                  <tr>
                    <td className="invoice-irn-label">IRN</td>
                    <td className="invoice-irn-value break-all">{note.irn || '—'}</td>
                  </tr>
                  <tr>
                    <td className="invoice-irn-label">Ack No.</td>
                    <td className="invoice-irn-value">{note.ackNo || '—'}</td>
                  </tr>
                  <tr>
                    <td className="invoice-irn-label">Ack Date</td>
                    <td className="invoice-irn-value">
                      {note.ackDate ? formatDate(String(note.ackDate)) : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="invoice-einvoice-right">
              <div className="invoice-section-heading">e-Invoice</div>
              {qrValue ? (
                <div className="bg-white border border-black p-1" style={{ width: '2in', height: '2in' }}>
                  <QRCode value={qrValue} style={{ width: '100%', height: '100%' }} />
                </div>
              ) : (
                <div className="invoice-party-line">QR not available</div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-6 mb-6 min-w-0">
          <div className="space-y-4 border rounded p-4 min-w-0">
            <div className="border-b pb-3">
              <div className="flex gap-4">
                {branchDetails && (
                  <>
                    <div className="flex-shrink-0">
                      <img src="/customer-logo/sg-group-logo.jpg" alt="SG Group" className="h-14 w-20 object-contain" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{branchDetails.branchName}</p>
                      <p className="text-xs text-gray-600 leading-snug">{branchDetails.address}</p>
                      <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {branchDetails.gstin || 'NA'}</p>
                      <p className="text-xs text-gray-600 leading-snug">
                        State Name: {branchDetails.stateName || 'NA'}, Code: {branchDetails.stateCode || 'NA'}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="border-b pb-3">
              <p className="font-semibold mb-2">Consignee (Ship to):</p>
              {shipToCustomer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{shipToCustomer.customerName}</p>
                  <p className="text-xs text-gray-600 leading-snug mt-0.5">{shipToCustomer.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">Vendor Code: {shipToCustomer.vendorCode ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {shipToCustomer.gstNo ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">
                    State Name: {shipToCustomer.stateName ?? 'NA'}, Code: {shipToCustomer.stateCode ?? 'NA'}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">—</p>
              )}
            </div>
            <div>
              <p className="font-semibold mb-2">Buyer (Bill to):</p>
              {billToCustomer ? (
                <div>
                  <p className="text-sm font-medium text-gray-900">{billToCustomer.customerName}</p>
                  <p className="text-xs text-gray-600 leading-snug mt-0.5">{billToCustomer.address}</p>
                  <p className="text-xs text-gray-600 leading-snug">Vendor Code: {billToCustomer.vendorCode ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">GSTIN/UIN: {billToCustomer.gstNo ?? 'NA'}</p>
                  <p className="text-xs text-gray-600 leading-snug">
                    State Name: {billToCustomer.stateName ?? 'NA'}, Code: {billToCustomer.stateCode ?? 'NA'}
                  </p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">—</p>
              )}
            </div>
          </div>

          <div className="invoice-meta-lines min-w-0 border border-gray-300 rounded overflow-hidden text-[11px] leading-snug divide-y divide-gray-300">
            <InvoiceStyleMetaRow label="e-Way Bill No.: " value={String(noteRec.ewayBillNo ?? '')} />
            <InvoiceStyleMetaRow
              label={`${docTitle} No.: `}
              value={String(
                note.noteNumber ?? (note as { creditDebitNoteNo?: string }).creditDebitNoteNo ?? (note.id != null ? `#${note.id}` : '')
              )}
            />
            <InvoiceStyleMetaRow
              label="Dated: "
              value={
                note.noteDate
                  ? formatDate(note.noteDate)
                  : (note as { creditDate?: string }).creditDate
                    ? formatDate((note as { creditDate: string }).creditDate)
                    : ''
              }
            />
            <InvoiceStyleMetaRow
              label="Original Invoice No. & Date: "
              value={
                [
                  note.referenceInvoiceNo ?? (note as { invoiceNo?: string }).invoiceNo,
                  (note as { invoiceDate?: string }).invoiceDate
                    ? formatDate((note as { invoiceDate: string }).invoiceDate)
                    : '',
                ]
                  .filter(Boolean)
                  .join(' dt. ') || ''
              }
            />
            <InvoiceStyleMetaRow label="Delivery Note: " value={String(noteRec.deliverNote ?? '')} />
            <InvoiceStyleMetaRow label="Reference No. & Date: " value={String(noteRec.referenceNoAndDate ?? '')} />
            <InvoiceStyleMetaRow
              label="Other References: "
              value={String((note as { otherReference?: string }).otherReference ?? '')}
            />
            <InvoiceStyleMetaRow label="Buyer's Order No.: " value={String(noteRec.buyerOrderNo ?? '')} />
            <InvoiceStyleMetaRow
              label="Buyer order dated: "
              value={
                (note as { buyerOrderDate?: string }).buyerOrderDate
                  ? formatDate((note as { buyerOrderDate: string }).buyerOrderDate)
                  : ''
              }
            />
            <InvoiceStyleMetaRow label="Dispatch Doc No.: " value={String(noteRec.dispatchDocNo ?? '')} />
            <InvoiceStyleMetaRow
              label="Delivery Note Date: "
              value={noteRec.deliverNoteDate ? formatDate(String(noteRec.deliverNoteDate)) : ''}
            />
            <InvoiceStyleMetaRow label="Dispatched through: " value={String(noteRec.dispatchedThrough ?? '')} />
            <InvoiceStyleMetaRow label="Destination: " value={String(noteRec.destination ?? '')} />
            <InvoiceStyleMetaRow label="Bill of Lading / LR-RR No.: " value={String(noteRec.billOfLanding ?? '')} />
            <InvoiceStyleMetaRow label="Motor Vehicle No.: " value={String(noteRec.motorVehicleNo ?? '')} />
            <InvoiceStyleMetaRow label="Term of delivery: " value={String(noteRec.termOfDelivery ?? '')} />
          </div>
        </div>
      </div>

      {/* BODY — line items (chunk); totals rows only on last chunk */}
      <div className="invoice-doc-body overflow-x-auto invoice-print-line-items">
        <table className="w-full max-w-full border-collapse border border-gray-300 text-center text-[11px] leading-tight table-fixed">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-300 px-1 py-1 w-[2rem]">#</th>
              <th className="border border-gray-300 px-1 py-1 w-[30%] min-w-0">
                Description of Goods
              </th>
              <th className="border border-gray-300 px-1 py-1 w-[3rem]">Boxes</th>
              <th className="border border-gray-300 px-1 py-1 w-[4.5rem] max-w-[4.5rem]">Heat No</th>
              <th className="border border-gray-300 px-1 py-1 w-[3.5rem]">HSN/SAC</th>
              <th className="border border-gray-300 px-1 py-1 w-[2.75rem]">Qty</th>
              <th className="border border-gray-300 px-1 py-1 w-[2.75rem]">Rate</th>
              <th className="border border-gray-300 px-1 py-1 w-[2.25rem]">Per</th>
              <th className="border border-gray-300 px-1 py-1 w-[3.25rem]">Amt</th>
            </tr>
          </thead>
          <tbody>
            {chunk.map((row, i) => {
              const ext = row as CreditDebitNoteDetailDto & {
                boxes?: unknown;
                heatNo?: string;
                userProvidedHeatNo?: string;
                packingDetailIds?: unknown[];
              };
              const boxesCell =
                ext.boxes != null && ext.boxes !== ''
                  ? String(ext.boxes)
                  : ext.packingDetailIds?.length
                    ? String(ext.packingDetailIds.length)
                    : '—';
              const heatCell = ext.userProvidedHeatNo || ext.heatNo || '—';
              return (
                <tr key={row.id ?? `r-${globalStart + i}`}>
                  <td className="border border-gray-300 px-1 py-0.5">{globalStart + i + 1}</td>
                  <td className="border border-gray-300 px-1 py-0.5 text-left break-words w-[30%] min-w-0">
                    {getCreditDebitNoteLineDescription(row)}
                  </td>
                  <td className="border border-gray-300 px-1 py-0.5">{boxesCell}</td>
                  <td className="border border-gray-300 px-1 py-0.5 break-all text-[10px]">{heatCell}</td>
                  <td className="border border-gray-300 px-1 py-0.5 break-all">{row.hsn ?? '—'}</td>
                  <td className="border border-gray-300 px-1 py-0.5">{row.qty}</td>
                  <td className="border border-gray-300 px-1 py-0.5">{row.rate}</td>
                  <td className="border border-gray-300 px-1 py-0.5">{row.uom ?? 'Nos'}</td>
                  <td className="border border-gray-300 px-1 py-0.5">
                    {(row.lineTotal ?? row.amount ?? row.qty * row.rate).toFixed(2)}
                  </td>
                </tr>
              );
            })}
            {!isLastChunk && (
              <tr className="bg-amber-50 text-[11px]">
                <td colSpan={9} className="border border-gray-300 px-1 py-1 text-center font-semibold italic">
                  continued to page number {nextPageNum}
                </td>
              </tr>
            )}
            {isLastChunk && (
              <>
                <tr className="text-[11px]">
                  <td colSpan={5} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                  <td className="border border-gray-300 px-1 py-0.5" />
                  <td colSpan={2} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                  <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                    {taxableAmount.toFixed(2)}
                  </td>
                </tr>
                {!isGst && (
                  <>
                    <tr className="text-[11px]">
                      <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                        CGST
                      </td>
                      <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                      <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                        {totalCgst.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="text-[11px]">
                      <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                        SGST
                      </td>
                      <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                      <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                        {totalSgst.toFixed(2)}
                      </td>
                    </tr>
                  </>
                )}
                {isGst && (
                  <tr className="text-[11px]">
                    <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                      IGST
                    </td>
                    <td colSpan={4} className="border border-gray-300 px-1 py-0.5 text-right font-semibold" />
                    <td className="border border-gray-300 px-1 py-0.5 text-right font-semibold">
                      {totalIgst.toFixed(2)}
                    </td>
                  </tr>
                )}
                <tr className="bg-gray-100 font-semibold text-[11px]">
                  <td colSpan={4} className="border border-gray-300 text-right py-1 px-1">
                    TOTAL
                  </td>
                  <td className="border border-gray-300 px-1 py-0.5" />
                  <td className="border border-gray-300 px-1 py-0.5 text-center">{totalQty}</td>
                  <td colSpan={2} className="border border-gray-300 px-1 py-0.5" />
                  <td className="border border-gray-300 px-1 py-0.5 text-right">₹ {grandTotal.toFixed(2)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* FOOTER — statutory block only after last line-item page of this copy */}
      {isLastChunk && (
        <div className="invoice-doc-footer">
          <p className="text-[11px] mt-2 text-right text-gray-600">E. & O.E</p>
          <div className="mt-3 border-t pt-3">
            <p className="text-xs text-gray-800 leading-snug">
              <strong>Amount Chargeable (in words):</strong> INR {amountInWords(grandTotal)}
            </p>
          </div>

          {hsnWiseTaxDetails.length > 0 && (
            <div className="mt-3 border rounded overflow-hidden">
              <table className="w-full border-collapse text-[11px] leading-tight">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">HSN/SAC</th>
                    <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">Taxable Value</th>
                    {!isGst ? (
                      <>
                        <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">CGST Rate</th>
                        <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">CGST Amt</th>
                        <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">SGST Rate</th>
                        <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">SGST Amt</th>
                      </>
                    ) : (
                      <>
                        <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">IGST Rate</th>
                        <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">IGST Amt</th>
                      </>
                    )}
                    <th className="border border-gray-300 px-1.5 py-1 text-center font-semibold">Total Tax Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {hsnWiseTaxDetails.map((row, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="border border-gray-300 px-1.5 py-1">{row.hsn}</td>
                      <td className="border border-gray-300 px-1.5 py-1">{row.taxableValue.toFixed(2)}</td>
                      {!isGst ? (
                        <>
                          <td className="border border-gray-300 px-1.5 py-1 text-center">
                            {toDisplayRate(row.cgstRate).toFixed(2)}%
                          </td>
                          <td className="border border-gray-300 px-1.5 py-1 text-right">{row.cgstAmount.toFixed(2)}</td>
                          <td className="border border-gray-300 px-1.5 py-1 text-center">
                            {toDisplayRate(row.sgstRate).toFixed(2)}%
                          </td>
                          <td className="border border-gray-300 px-1.5 py-1 text-right">{row.sgstAmount.toFixed(2)}</td>
                        </>
                      ) : (
                        <>
                          <td className="border border-gray-300 px-1.5 py-1 text-center">
                            {toDisplayRate(row.igstRate).toFixed(2)}%
                          </td>
                          <td className="border border-gray-300 px-1.5 py-1 text-right">{row.igstAmount.toFixed(2)}</td>
                        </>
                      )}
                      <td className="border border-gray-300 px-1.5 py-1 text-right">{row.totalTaxAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="border border-gray-300 px-1.5 py-1">TOTAL</td>
                    <td className="border border-gray-300 px-1.5 py-1">{taxableAmount.toFixed(2)}</td>
                    {!isGst ? (
                      <>
                        <td className="border border-gray-300 px-1.5 py-1" />
                        <td className="border border-gray-300 px-1.5 py-1 text-right">{totalCgst.toFixed(2)}</td>
                        <td className="border border-gray-300 px-1.5 py-1" />
                        <td className="border border-gray-300 px-1.5 py-1 text-right">{totalSgst.toFixed(2)}</td>
                      </>
                    ) : (
                      <>
                        <td className="border border-gray-300 px-1.5 py-1" />
                        <td className="border border-gray-300 px-1.5 py-1 text-right">{totalIgst.toFixed(2)}</td>
                      </>
                    )}
                    <td className="border border-gray-300 px-1.5 py-1 text-right">{totalTaxAmount.toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 border rounded px-2 py-1">
            <p className="text-xs text-gray-800 leading-snug">
              <strong>Tax Amount (in words):</strong> INR {amountInWords(totalTaxAmount)}
            </p>
            <p className="text-xs font-semibold text-gray-900 mt-1.5">Company&apos;s PAN: {branchDetails?.panNo ?? 'NA'}</p>
          </div>

          <div className="flex justify-between gap-4 border-t items-start mt-3 pt-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-gray-900">Declaration:</p>
              <p className="text-xs text-gray-700 leading-snug mt-0.5">
                We declare that this note shows the actual price of the goods described and that all particulars are true and
                correct.
              </p>
            </div>
            <div className="text-right shrink-0 min-w-[11rem]">
              {einvoiceSignerName.trim() ? (
                <div className="text-gray-900">
                  {branchDisplayName ? (
                    <p className="text-xs font-bold text-right mb-1.5">for {branchDisplayName}</p>
                  ) : null}
                  <div className="flex items-start justify-end gap-1.5">
                    <CircleCheckBig className="invoice-digital-sign-tick h-6 w-6 shrink-0" aria-hidden />
                    <div className="text-right text-[10px] leading-tight text-gray-900">
                      <div className="font-normal">{einvoiceSignerName.trim()}</div>
                      {digitallySignedOn?.trim() ? (
                        <div className="text-[9px] leading-tight text-gray-700 mt-0.5">
                          Digitally signed on {digitallySignedOn.trim()}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs font-bold text-right mt-2 border-b border-black pb-0.5">Authorised Signatory</p>
                </div>
              ) : (
                <p className="text-xs font-medium text-gray-900">Authorized Signatory</p>
              )}
            </div>
          </div>

          <div className="invoice-print-doc-footer mt-4 pt-2 border-t border-gray-300 text-center text-[11px] leading-snug text-gray-600 space-y-0.5">
            <p className="font-medium">SUBJECT TO JAIPUR JURISDICTION</p>
            <p>This is a Computer Generated {docTitle}</p>
          </div>
        </div>
      )}
    </div>
  );
}
